import React, { useEffect } from 'react';
import { Col, ListGroup, Row } from 'react-bootstrap';
import {
  MemoryDTO,
  MemoryLinkValue,
  MemoryValue,
  StepCheck,
  StepCheckOperatorType,
  StepCheckSwitch,
  CheckOperator,
  PlayerHasMatchType,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { Branch } from 'dorian-shared/types/branch/Branch';
import { Character } from 'dorian-shared/types/character/Character';
import { MemoryBankCheckValue, MemorySlotCompareForm } from '../MemorySlotCompareForm';
import { MultiMemoryCheckForm } from '../MultiMemoryCheckForm/MultiMemoryCheckForm';
import { MultiMemoryCheckSwitchForm } from '../MultiMemoryCheckForm/MultiMemoryCheckSwitchForm';
import { ConditionListForm } from '../ConditionListForm/ConditionListForm';
import { PlayerHasAchievementForm } from '../PlayerHasAchievementForm/PlayerHasAchievementForm';
import { StepTypeCheckSwitchForm, SWITCH_INDEX_TYPE } from './StepTypeCheckSwitchForm';

/**
 * Gets the check value excluding number[] (used only by playerHas).
 * Returns the value if it's a MemoryValue or MemoryLinkValue, otherwise returns empty string.
 */
const getCheckValue = (value: StepCheck['value']): MemoryValue | MemoryLinkValue => {
  if (Array.isArray(value)) {
    return '';
  }
  return value ?? '';
};

export function isCheckStep(branch: Branch) {
  return branch.switch && branch.switch.length > 0;
}

type StepTypeCheckProps = {
  stepIndex: number,
  memoryBankSlots: MemoryDTO[],
  branches: Branch[],
  currentBranchId: number,
  checkData: StepCheck,
  switchData: StepCheckSwitch[],
  addStepCheck: (
    stepIndex: number,
    variableId: number,
    operatorName: string,
    value: MemoryValue,
  ) => void,
  handleChangeStepCheck: (
    values: MemoryBankCheckValue[],
    stepIndex: number,
  ) => void,
  handleChangeStepSwitch: (
    gotoBranchId: number,
    stepIndex: number,
    switchIndex: number,
  ) => void,
  handleAddStepSwitchCondition?: (stepIndex: number) => void,
  handleRemoveStepSwitchCondition?: (stepIndex: number, switchIndex: number) => void,
  handleReorderStepSwitchConditions?: (stepIndex: number, oldIndex: number, newIndex: number) => void,
  handleChangeStepSwitchCondition?: (operator: string, intValue: string, stepIndex: number, switchIndex: number) => void,
  handleChangeStepSwitchVariableCondition?: (operator: string, varValue: number, stepIndex: number, switchIndex: number) => void,
  characters: Character[],
  isDisabled?: boolean,
  isMoreFunctions: boolean,
}

export function StepTypeCheck(props: StepTypeCheckProps) {
  const {
    stepIndex, memoryBankSlots, handleChangeStepCheck, handleChangeStepSwitch,
    checkData, switchData, branches, currentBranchId, addStepCheck, characters,
    isDisabled, isMoreFunctions, handleAddStepSwitchCondition, handleRemoveStepSwitchCondition,
    handleReorderStepSwitchConditions, handleChangeStepSwitchCondition, handleChangeStepSwitchVariableCondition,
  } = props;

  const currentSlot = memoryBankSlots.find((slot) => slot.id === Number(checkData?.variableId));
  const switchTrueGotoBranchId = switchData
    && switchData[SWITCH_INDEX_TYPE.TRUE] ? switchData[SWITCH_INDEX_TYPE.TRUE].gotoBranchId : 0;
  const switchFalseGotoBranchId = switchData
    && switchData[SWITCH_INDEX_TYPE.FALSE] ? switchData[SWITCH_INDEX_TYPE.FALSE].gotoBranchId : 0;

  const isCheckStepPossible = memoryBankSlots && memoryBankSlots.length > 0;
  const isConditionList = checkData && CheckOperator[checkData.operator]?.isConditionList === true;
  const isPlayerHas = checkData?.operator === StepCheckOperatorType.PlayerHas;

  useEffect(() => {
    if (!isCheckStepPossible) {
      return;
    }

    if (!checkData) {
      const variable = memoryBankSlots[0];
      let operator = StepCheckOperatorType.Equal;
      if (isMoreFunctions) {
        operator = StepCheckOperatorType.Min;
      }
      const value = '';
      addStepCheck(stepIndex, variable.id, operator, value);
    }
  }, [checkData, addStepCheck, isCheckStepPossible, memoryBankSlots, stepIndex, isMoreFunctions]);

  if (!checkData) {
    return null;
  }

  return (
    <>
      {!isMoreFunctions && !isPlayerHas && (
      <Col>
        <MemorySlotCompareForm
          variableInputValue={checkData.variableId}
          operatorInputValue={checkData.operator}
          checkInputValue={getCheckValue(checkData.value)}
          currentSlot={currentSlot}
          inputNamePrefix={`steps[${stepIndex}][check]`}
          memoryBankSlots={memoryBankSlots}
          stepIndex={stepIndex}
          characters={characters}
          onChangeValue={handleChangeStepCheck}
          isDisabled={isDisabled}
        />
        <Row>
          <StepTypeCheckSwitchForm
            stepIndex={stepIndex}
            switchIndex={SWITCH_INDEX_TYPE.TRUE}
            gotoBranchId={switchTrueGotoBranchId}
            currentBranchId={currentBranchId}
            handleChangeStepSwitch={handleChangeStepSwitch}
            branches={branches}
            isDisabled={isDisabled}
          />
          <StepTypeCheckSwitchForm
            stepIndex={stepIndex}
            switchIndex={SWITCH_INDEX_TYPE.FALSE}
            gotoBranchId={switchFalseGotoBranchId}
            currentBranchId={currentBranchId}
            handleChangeStepSwitch={handleChangeStepSwitch}
            branches={branches}
            isDisabled={isDisabled}
          />
        </Row>
      </Col>
      )}
      {!isMoreFunctions && isPlayerHas && (
        <Col>
          <PlayerHasAchievementForm
            inputNamePrefix={`steps[${stepIndex}][check]`}
            checkInputValue={checkData.value as number[]}
            operatorInputValue={checkData.operator}
            matchInputValue={checkData.match ?? PlayerHasMatchType.All}
            stepIndex={stepIndex}
            onChangeValue={handleChangeStepCheck}
            isDisabled={isDisabled}
          />
          <Row>
            <StepTypeCheckSwitchForm
              stepIndex={stepIndex}
              switchIndex={SWITCH_INDEX_TYPE.TRUE}
              gotoBranchId={switchTrueGotoBranchId}
              currentBranchId={currentBranchId}
              handleChangeStepSwitch={handleChangeStepSwitch}
              branches={branches}
              isDisabled={isDisabled}
            />
            <StepTypeCheckSwitchForm
              stepIndex={stepIndex}
              switchIndex={SWITCH_INDEX_TYPE.FALSE}
              gotoBranchId={switchFalseGotoBranchId}
              currentBranchId={currentBranchId}
              handleChangeStepSwitch={handleChangeStepSwitch}
              branches={branches}
              isDisabled={isDisabled}
            />
          </Row>
        </Col>
      )}
      {isMoreFunctions && isPlayerHas && (
        <Col>
          <PlayerHasAchievementForm
            inputNamePrefix={`steps[${stepIndex}][check]`}
            checkInputValue={checkData.value as number[]}
            operatorInputValue={checkData.operator}
            matchInputValue={checkData.match ?? PlayerHasMatchType.All}
            stepIndex={stepIndex}
            onChangeValue={handleChangeStepCheck}
            isDisabled={isDisabled}
          />
          <Row>
            <StepTypeCheckSwitchForm
              stepIndex={stepIndex}
              switchIndex={SWITCH_INDEX_TYPE.TRUE}
              gotoBranchId={switchTrueGotoBranchId}
              currentBranchId={currentBranchId}
              handleChangeStepSwitch={handleChangeStepSwitch}
              branches={branches}
              isDisabled={isDisabled}
            />
            <StepTypeCheckSwitchForm
              stepIndex={stepIndex}
              switchIndex={SWITCH_INDEX_TYPE.FALSE}
              gotoBranchId={switchFalseGotoBranchId}
              currentBranchId={currentBranchId}
              handleChangeStepSwitch={handleChangeStepSwitch}
              branches={branches}
              isDisabled={isDisabled}
            />
          </Row>
        </Col>
      )}
      {isMoreFunctions && !isConditionList && !isPlayerHas && (
        <Col>
          <MultiMemoryCheckForm
            operatorInputValue={checkData.operator}
            checkInputValue={getCheckValue(checkData.value)}
            inputNamePrefix={`steps[${stepIndex}][check]`}
            memoryBankSlots={memoryBankSlots}
            stepIndex={stepIndex}
            onChangeValue={handleChangeStepCheck}
            isDisabled={isDisabled}
          />
          <Col>
            <ListGroup>
              {switchData?.map((data, index) => {
                const memoryName = memoryBankSlots.find((memory) => Number(memory.id) === Number(data.value))?.name ?? 'Unknown';
                return (
                  <ListGroup.Item key={data.value as string} style={{ marginBottom: 0 }}>
                    <MultiMemoryCheckSwitchForm
                      stepIndex={stepIndex}
                      switchIndex={index}
                      gotoBranchId={data.gotoBranchId}
                      currentBranchId={currentBranchId}
                      handleChangeStepSwitch={handleChangeStepSwitch}
                      branches={branches}
                      isDisabled={isDisabled}
                      value={data.value as string}
                      label={memoryName}
                    />
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </Col>
        </Col>
      )}
      {isMoreFunctions && isConditionList && handleAddStepSwitchCondition && handleChangeStepSwitchCondition && handleChangeStepSwitchVariableCondition && handleRemoveStepSwitchCondition && (
        <ConditionListForm
          inputNamePrefix={`steps[${stepIndex}][check]`}
          operatorInputValue={checkData.operator}
          checkInputValue={checkData.variableId || getCheckValue(checkData.value)}
          memoryBankSlots={memoryBankSlots}
          stepIndex={stepIndex}
          onChangeValue={handleChangeStepCheck}
          isDisabled={isDisabled}
          switchData={switchData}
          branches={branches}
          currentBranchId={currentBranchId}
          handleChangeStepSwitch={handleChangeStepSwitch}
          handleChangeCondition={handleChangeStepSwitchCondition}
          handleChangeVariableCondition={handleChangeStepSwitchVariableCondition}
          onAddCondition={handleAddStepSwitchCondition}
          onRemoveCondition={handleRemoveStepSwitchCondition}
          onReorderConditions={handleReorderStepSwitchConditions}
        />
      )}
    </>
  );
}
